import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import Zoom from '@mui/material/Zoom';
import { toast } from 'react-toastify';
import { httpGet, httpPost, httpPut, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import TaxMaps from './TaxMaps';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridActionsCellItem } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Close';
import { GridRowModes, useGridApiRef } from '@mui/x-data-grid';
import RemoveIcon from '@mui/icons-material/Remove';
import Checkbox from '@mui/material/Checkbox';
import { Check } from '@mui/icons-material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { getFilter, setFilter } from '../Common/Utilities';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

export default function Taxes(props) {
    const isMediaOcean = (localStorage.getItem('systemType') || '').trim().toLowerCase() === 'mediaoceanprisma';
    const [filterText, setFilterText] = React.useState(getFilter(['SystemSetup', 'Taxes', 'searchText']) || '');
    const apiGridRef = useGridApiRef();
    const [data, setData] = React.useState([]);
    const [openTaxMapsDialog, setOpenTaxMapsDialog] = React.useState(false);
    const [lastUpdated, setLastUpdated] = React.useState("");
    const [rowModesModel, setRowModesModel] = React.useState({});
    const [currentEditRowId, setCurrentEditRowId] = React.useState(null);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
    const [companyCode, setCompanyCode] = React.useState(null);
    const [selectedTaxTypeCode, setSelectedTaxTypeCode] = React.useState(null);
    const [isAdding, setIsAdding] = React.useState(null);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState(false);
    const [deleteRowData, setDeleteRowData] = React.useState(null);
    const [sortModel, setSortModel] = React.useState(getFilter(['SystemSetup', 'Taxes', 'sortModel']) || []);

    React.useEffect(() => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const companyCode = userInfo ? userInfo.companyCode : null;

        const gridRef = apiGridRef?.current;
        if (gridRef && typeof gridRef.setQuickFilterValues === 'function') {
            gridRef.setQuickFilterValues([filterText]);
        }
        setCompanyCode(companyCode);
        fetchTaxes();
    }, []);

    const fetchTaxes = React.useCallback(() => {

        props.inProgress(true);

        httpGet('/api/setup/taxes')
            .then((response) => {
                const data = response.data;
                const records = (data.records || []).map((e, i) => ({ ...e, rowId: i }));
                const localDateString = data.lastUpdated == null ? "" : new Date(data.lastUpdated).toLocaleString();
                setData(records);
                setLastUpdated(localDateString);
                props.inProgress(false);
            })
            .catch((error) => {
                props.inProgress(false);

                toast.error(`An error occurred while fetching the records: ${error.message}`);
            });
    }, [props]);

    const isRecordValid = (data, existingData) => {
        let isValid = true;
        let message = "";
        const newTaxCode = data.taxCode?.trim().toLowerCase();
        const newTaxTypeCode = data.taxTypeCode?.trim().toLowerCase();
        const taxExists = existingData.some(row =>
            row.taxCode.trim().toLowerCase() === newTaxCode &&
            row.taxTypeCode.trim().toLowerCase() === newTaxTypeCode &&
            row.rowId !== data.rowId
        );

        if (taxExists) {
            toast.error('This tax code and tax type code combination already exists');
            return false;
        }
        if (!data.taxCode || data.taxCode.trim().length === 0) {
            message += "Tax Code, ";
        }
        if (!data.otherTaxAmt || isNaN(data.otherTaxAmt)) {
            message += "Tax Amount, ";
        }
        if (!data.taxTypeCode || data.taxTypeCode.trim().length === 0) {
            message += "Tax Type Code, ";
        }
        if (message.length > 0) {
            message = message.slice(0, -2);
            toast.error(`Please provide ${message}`);
            isValid = false;
        }
        return isValid;
    };

    const handleAddClick = React.useCallback(() => {
        const id = ([...data]?.sort((a, b) => b.rowId - a.rowId)[0]?.rowId + 1) || 1;
        let model = {
            rowId: id,
            isNew: true
        };
        setData((prevData) => [model, ...prevData]);
        setCurrentEditRowId(id);
        setRowModesModel((prevModel) => ({ ...prevModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'taxCode' } }));
        setIsButtonDisabled(true);
        setIsAdding(true);
    }, [data, currentEditRowId]);

    const handleSaveClick = (newData) => {
        setRowModesModel({ ...rowModesModel, [newData.rowId]: { mode: GridRowModes.View } });
    };

    const saveTaxes = React.useCallback((newdata, oldData) => {
        let rowdata = structuredClone(newdata);
        let endpoint = "/api/setup/savetax";

        rowdata.companyCode = parseInt(companyCode);

        props.inProgress(true);

        httpPost(endpoint, rowdata)
            .then((response) => {
                props.inProgress(false);
                newdata.isNew ? toast.success('Added Successfully') : toast.success('Updated Successfully');
                fetchTaxes();
                setIsButtonDisabled(false);
            })
            .catch((error) => {
                if (newdata?.isNew) {
                    setData((prev) => prev.filter(e => e.rowId !== newdata.rowId));
                } else {
                    setData((prev) => prev.map(e => e.rowId === newdata.rowId ? oldData : e));
                }
                props.inProgress(false);
                toast.error(<div>Taxes Update Failed<br />{error.response.data}</div>);
                setIsButtonDisabled(false);
            });
    }, [companyCode, fetchTaxes, props]);

    const handleDeleteClick = React.useCallback((rowData) => {
        setDeleteRowData(rowData);
        setOpenDeleteConfirmation(true);
    }, []);

    const closeDeleteDialog = React.useCallback(() => {
        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);
    }, []);

    const deleteTaxes = React.useCallback(() => {
        props.inProgress(true);
        const data = deleteRowData;
        const taxCode = encodeURIComponent(data.taxCode);
        const taxTypeCode = encodeURIComponent(data.taxTypeCode);
        const url = `/api/setup/deletetax?taxCode=${taxCode}&taxTypeCode=${taxTypeCode}`;

        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);

        httpDelete(url)
            .then((response) => {
                props.inProgress(false);
                toast.success('Deleted Successfully');
                fetchTaxes();
            })
            .catch((error) => {
                props.inProgress(false);
                if (error.response.data.includes("Cannot delete, record being referenced by other entities.")) {
                    toast.error("Cannot delete, record being referenced by other entities.");
                } else {
                    toast.error(<div>Delete Failed<br />{error.response.data}</div>);
                }
            });
    }, [deleteRowData, fetchTaxes]);


    const processRowUpdate = React.useCallback((newrow, oldrow) => {
        let _newrow = structuredClone(newrow);
        if (!isRecordValid(_newrow, data)) {
            setRowModesModel({ ...rowModesModel, [_newrow.rowId]: { mode: GridRowModes.Edit } });
            throw new Error('');
        }
        setCurrentEditRowId(null);
        saveTaxes(_newrow, oldrow);
        return newrow;
    }, [saveTaxes]);

    const handleCancelClick = React.useCallback((id) => {
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
        setCurrentEditRowId(null);
        setIsButtonDisabled(false);
        setIsAdding(false);
        setData((prevData) => prevData.filter((row) => !(row.rowId === id && row.isNew)));
    }, []);

    const handleEditClick = React.useCallback((id) => {
        setCurrentEditRowId(id);
        setIsButtonDisabled(true);
        editable: false;
        setIsAdding(false);
        setRowModesModel((prevModel) => ({ ...prevModel, [id]: { mode: GridRowModes.Edit } }));
    }, []);

    const editTaxMaps_clickHandler = (event) => {
        event.preventDefault();
        setOpenTaxMapsDialog(true);
        setSelectedTaxTypeCode(event.target.dataset.taxtypecode);
    };

    const taxMapsDialog_closeHandler = () => {
        setOpenTaxMapsDialog(false);
    };

    const getAddBoxButton = () => (
        <IconButton
            variant="contained"
            onClick={handleAddClick}
            size="small"
            style={{ float: "right", zIndex: 1, marginTop: 10, display: 'block' }}
            disabled={isButtonDisabled}
        >
            <Tooltip key={1} title={'add'}><AddBoxIcon key={2} /></Tooltip>
        </IconButton>
    );

    const info = {
        taxTypeCode: selectedTaxTypeCode,
        inProgress: props.inProgress
    };

    return (
        <div>
            <h1>Taxes</h1>
            <div className="form-group">
                <div style={{ display: 'block', overflow: 'auto', width: '100%' }}>
                    <EnhancedDataGrid
                        rows={data}
                        apiRef={apiGridRef}
                        disableColumnMenu
                        pagination
                        pageSize={10}
                        editMode="row"
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(err) => { }}
                        title={"Last Updated: " + lastUpdated}
                        disableSelectionOnClick
                        customcomponent={<>{getAddBoxButton()}</>}
                        rowModesModel={rowModesModel}
                        setFilterText={(text) => {
                            const trimmed = text?.trim() || '';
                            setFilterText(trimmed);
                            setFilter(['SystemSetup', 'Taxes', 'searchText'], trimmed);
                        }}
                        filterText={filterText}
                        onSortModelChange={(model) => {
                            setSortModel(model);
                            setFilter(['SystemSetup', 'Taxes', 'sortModel'], model);
                        }}
                        sortModel={sortModel}
                        columns={[
                            {
                                headerName: 'Tax',
                                field: 'taxCode',
                                flex: 0.5,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: isAdding,
                            },
                            {
                                headerName: 'Tax Description',
                                field: 'taxDesc',
                                flex: 1,
                                editable: true,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                            },
                            {
                                headerName: 'Tax Type',
                                field: 'taxTypeCode',
                                flex: 0.5,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: isAdding,
                            },
                            {
                                headerName: 'Tax Type Description',
                                field: 'taxTypeDesc',
                                flex: 1,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: true,
                            },
                            {
                                headerName: 'Tax Category',
                                field: 'taxType',
                                flex: 0.7,
                                valueFormatter: ({ value }) => value === 1 ? 'State Tax' : value === 2 ? 'Local Tax' : '',
                                renderEditCell: ({ id, value, field, api }) => (
                                    <select
                                        value={value ?? ''}
                                        onChange={(event) => {
                                            const selectedValue = event.target.value === '' ? null : Number(event.target.value);
                                            api.setEditCellValue({ id, field, value: selectedValue });
                                        }}
                                        style={{ width: '100%', height: '100%', border: 0 }}
                                    >
                                        <option value="">Unclassified</option>
                                        <option value="1">State Tax</option>
                                        <option value="2">Local Tax</option>
                                    </select>
                                ),
                                editable: true,
                            },
                            {
                                headerName: 'Tax Amount (%)',
                                field: 'otherTaxAmt',
                                flex: 0.5,
                                align: 'right',
                                headerAlign: 'right',
                                type: "numeric",
                                editable: true,
                                ClassName: "MuiInputBase-input MuiInput-input",
                            },
                            {
                                headerName: 'Is Tax Included',
                                field: 'taxIncludedFlag',
                                type: 'boolean',
                                width: 80,
                                headerAlign: 'center',
                                align: 'center',
                                renderCell: (props) => (
                                    props.value ? (
                                        <Check key={props.id} color={'action'} />
                                    ) : (
                                        <RemoveIcon key={props.id} color={'action'} />
                                    )
                                ),
                                renderEditCell: (props) => (
                                    <Checkbox
                                        key={props.row.rowId}
                                        color="default"
                                        checked={Boolean(props.value)}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            const { id, api, field } = props;
                                            api.setEditCellValue({ id, field, value: newValue });
                                        }}
                                    />
                                ),
                                editable: true,
                            },
                            {
                                headerName: 'Is Tax Percentage',
                                field: 'prcFlag',
                                type: 'boolean',
                                width: 80,
                                headerAlign: 'center',
                                align: 'center',
                                renderCell: (props) => (
                                    Boolean(props.value) ? (
                                        <Check key={props.id} color={'action'} />
                                    ) : (
                                        <RemoveIcon key={props.id} color={'action'} />
                                    )
                                ),
                                renderEditCell: (props) => (
                                    <Checkbox
                                        key={props.row.rowId}
                                        color="default"
                                        checked={Boolean(props.value)}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            const { id, api, field } = props;
                                            api.setEditCellValue({ id, field, value: newValue });
                                        }}
                                    />
                                ),
                                editable: true,
                            },
                            {
                                headerName: 'Mappings',
                                headerAlign: 'center',
                                align: 'center',
                                field: 'mappings',
                                resizable: false,
                                sortable: false,
                                width: 150,
                                renderCell: (rowData) => {
                                    if (rowData.row.isNew) {
                                        return '';
                                    }
                                    return <Link data-taxtypecode={rowData.row.taxTypeCode} href="#" onClick={editTaxMaps_clickHandler}>Edit</Link>;
                                },
                            },
                            {
                                field: 'actions',
                                type: 'actions',
                                headerName: 'Actions',
                                width: 120,
                                cellClassName: 'actions',
                                getActions: (rowData) => {
                                    const isInEditMode = rowModesModel[rowData.id]?.mode === GridRowModes.Edit;
                                    if (isInEditMode) {
                                        return [
                                            <GridActionsCellItem
                                                key={rowData.id + 1}
                                                icon={<Tooltip key={rowData.id + 2} title={'Save'}><DoneIcon key={rowData.id + 1} /></Tooltip>}
                                                label="Save"
                                                color="inherit"
                                                onClick={() => { handleSaveClick(rowData.row) }}
                                            />,
                                            <GridActionsCellItem
                                                key={rowData.id + 2}
                                                icon={<Tooltip key={rowData.id + 2} title={'Cancel'}><CancelIcon key={rowData.id + 1} /></Tooltip>}
                                                label="Cancel"
                                                className="textPrimary"
                                                onClick={() => { handleCancelClick(rowData.id) }}
                                                color="inherit"
                                            />,
                                        ];
                                    }
                                    return [
                                        <GridActionsCellItem
                                            key={rowData.id + 2}
                                            icon={<Tooltip key={rowData.id + 2} title={'Edit'}><EditIcon key={rowData.id + 1} /></Tooltip>}
                                            label="Edit"
                                            className="textPrimary"
                                            onClick={() => { handleEditClick(rowData.id) }}
                                            color="inherit"
                                            disabled={isButtonDisabled}
                                        />,
                                        <GridActionsCellItem
                                            key={rowData.id + 3}
                                            icon={<Tooltip key={rowData.id + 2} title={'Delete'}><DeleteIcon key={rowData.id + 1} /></Tooltip>}
                                            label="Delete"
                                            onClick={() => { handleDeleteClick(rowData.row) }}
                                            color="inherit"
                                            disabled={isButtonDisabled}
                                        />,
                                    ];
                                }
                            }
                        ]}
                        getRowId={(row) => row.rowId}
                        editable={
                            {
                                onRowAdd: newData =>
                                    new Promise((resolve, reject) => {
                                        newData.rowId = 0;
                                        newData.companyCode = parseInt(state.companyCode);

                                        if (isRecordValid(newData)) {
                                            saveTaxes(newData, resolve, reject);
                                        }
                                        else {
                                            reject();
                                        }
                                    }),
                                onRowUpdate: (newData, oldData) =>
                                    new Promise((resolve, reject) => {

                                        if (isRecordValid(newData)) {
                                            saveTaxes(newData, resolve, reject);
                                        }
                                        else {
                                            reject();
                                        }
                                    }),
                                onRowDelete: oldData =>
                                    new Promise((resolve, reject) => {
                                        deleteTaxes(oldData, resolve, reject);
                                    }),
                                onRowAddCancelled: rowData =>
                                    new Promise((resolve, reject) => {
                                    }),
                                onRowUpdateCancelled: rowData =>
                                    new Promise((resolve, reject) => {
                                    })
                            }
                        }
                    />
                </div>
            </div>
            <Dialog open={openTaxMapsDialog} TransitionComponent={Transition} fullWidth maxWidth="lg">
                <DialogContent>
                    <TaxMaps info={info} onClose={taxMapsDialog_closeHandler} />
                </DialogContent>
            </Dialog>

            <Dialog
                open={openDeleteConfirmation}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Record
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the tax code "{deleteRowData ? deleteRowData.taxCode : ""}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteTaxes} color="primary">
                        Yes
                    </Button>
                    <Button autoFocus onClick={closeDeleteDialog} color="primary">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}